import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { v4 as makeUuid } from 'uuid'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { isFailed, isSuccess } from 'redux/toolkit/api'
import { createAuthenticationProcess } from 'redux/features/oauth2/oauth2Slice'
import routesConfig from 'lib/routesConfig'
import appConfig from 'config/appConfig'
import { OAuth2Connection } from 'types/oauth2'

/**
 * Build APIGW authorize URL with optional Login App params.
 * Only includes params when present (backward compatible).
 */
const buildAuthorizeUrl = (
  authProcessId: string,
  redirectUri: string,
  connection: string,
  accountHint: string | null,
  nav: string | null,
  entryPath: string | null,
  correlationId: string | null,
  cplEnabled: string | null
): string => {
  const url = new URL(`${appConfig.GATEWAY_URL}/internal/oauth2/authorize`)
  url.searchParams.set('auth_process_id', authProcessId)
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('connection', connection)

  // Login App params (optional, only include when present)
  if (accountHint) {
    url.searchParams.set('account_hint', accountHint)
  }
  if (nav) {
    url.searchParams.set('nav', nav)
  }
  if (entryPath) {
    url.searchParams.set('entry_path', entryPath)
  }

  // X2: TDF pass check circuit breaker params
  if (correlationId) {
    url.searchParams.set('correlation_id', correlationId)
  }
  if (cplEnabled) {
    url.searchParams.set('cpl_enabled', cplEnabled)
  }

  return url.toString()
}

export const useOAuth2AuthorizeLogic = () => {
  const dispatch = useAppDispatch()
  const [params] = useSearchParams()

  // Standard OAuth2 params
  const redirect_uri = params.get('redirect_uri') || `${window.location.origin}/webui/oauth2/callback`
  const connection = params.get('connection') || OAuth2Connection.MS_ENTRA

  // Login App params (optional, for account context)
  // - account_hint: Simple account ID from EGD-internal flows (account switch, etc.)
  // - nav: Signed JWT from external systems (product switcher, deep links)
  // - entry_path: Where to redirect after session creation (e.g., "/webui/admin")
  const account_hint = params.get('account_hint')
  const nav = params.get('nav')
  const entry_path = params.get('entry_path')

  // X2: TDF pass check circuit breaker params
  // - correlation_id: TDF-generated tracing ID for pass check flow
  // - cpl_enabled: "0" means TDF detected loop, don't route back to TDF
  const correlation_id = params.get('correlation_id')
  const cpl_enabled = params.get('cpl_enabled')

  const { isCreateProcessSuccess, isCreateProcessFailed, authProcess } = useAppSelector(_store => ({
    isCreateProcessSuccess: isSuccess(_store.oauth2.api.createAuthenticationProcessApiStatus),
    isCreateProcessFailed: isFailed(_store.oauth2.api.createAuthenticationProcessApiStatus),
    authProcess: _store.oauth2.createAuthenticationProcessResponse
  }))

  useEffect(() => {
    const authProcessId = makeUuid()
    dispatch(createAuthenticationProcess({ authProcessId }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isCreateProcessSuccess && authProcess) {
      window.location.href = buildAuthorizeUrl(
        authProcess.id,
        redirect_uri,
        connection,
        account_hint,
        nav,
        entry_path,
        correlation_id,
        cpl_enabled
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateProcessSuccess])

  useEffect(() => {
    if (isCreateProcessFailed) {
      routesConfig.LOGIN.goto(undefined, false, '?error=OAuth2AuthorizationCodeRequestFailed')
    }
  }, [isCreateProcessFailed])
}
